import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { userConfigurationStorage } from "../userConfigurationStorage.js";

export type Grouping = "source" | "none";
export type FontSize = "md" | "lg" | "xl";
export type FontWeight = "regular" | "medium";

export const FONT_SIZE_VALUES: Record<FontSize, string> = {
  md: "13.5px",
  lg: "15px",
  xl: "17px",
};

export const FONT_WEIGHT_VALUES: Record<FontWeight, string> = {
  regular: "400",
  medium: "500",
};

const DEFAULT_FONT_SIZE: FontSize = "md";
const DEFAULT_FONT_WEIGHT: FontWeight = "regular";

interface ViewSettingsCtx {
  hideRead: boolean;
  columnCount: number;
  grouping: Grouping;
  fontSize: FontSize;
  fontWeight: FontWeight;
  setHideRead: (v: boolean) => void;
  setColumnCount: (n: number) => void;
  setGrouping: (v: Grouping) => void;
  setFontSize: (v: FontSize) => void;
  setFontWeight: (v: FontWeight) => void;
  resetToDefaults: () => void;
}

const ViewSettingsContext = createContext<ViewSettingsCtx>({
  hideRead: true,
  columnCount: 1,
  grouping: "source",
  fontSize: DEFAULT_FONT_SIZE,
  fontWeight: DEFAULT_FONT_WEIGHT,
  setHideRead: () => {},
  setColumnCount: () => {},
  setGrouping: () => {},
  setFontSize: () => {},
  setFontWeight: () => {},
  resetToDefaults: () => {},
});

function defaultColumnCount(): number {
  for (let n = 4; n >= 1; n--) {
    if (window.innerWidth / n > 600) return n;
  }
  return 1;
}

function isFontSize(v: string): v is FontSize {
  return v === "md" || v === "lg" || v === "xl";
}
function isFontWeight(v: string): v is FontWeight {
  return v === "regular" || v === "medium";
}

export function ViewSettingsProvider({ children }: { children: ReactNode }) {
  const [hideRead, setHideRead] = useState(true);
  const [columnCount, setColumnCount] = useState(defaultColumnCount);
  const [grouping, setGrouping] = useState<Grouping>("source");

  const [fontSize, setFontSize] = useState<FontSize>(() => {
    const saved = userConfigurationStorage.postFontSize.get();
    return saved && isFontSize(saved) ? saved : DEFAULT_FONT_SIZE;
  });
  const [fontWeight, setFontWeight] = useState<FontWeight>(() => {
    const saved = userConfigurationStorage.postFontWeight.get();
    return saved && isFontWeight(saved) ? saved : DEFAULT_FONT_WEIGHT;
  });

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--post-font-size", FONT_SIZE_VALUES[fontSize]);
    root.style.setProperty("--post-font-weight", FONT_WEIGHT_VALUES[fontWeight]);
    userConfigurationStorage.postFontSize.set(fontSize);
    userConfigurationStorage.postFontWeight.set(fontWeight);
  }, [fontSize, fontWeight]);

  function resetToDefaults() {
    setHideRead(true);
    setColumnCount(defaultColumnCount());
    setGrouping("source");
    setFontSize(DEFAULT_FONT_SIZE);
    setFontWeight(DEFAULT_FONT_WEIGHT);
  }

  return (
    <ViewSettingsContext.Provider
      value={{
        hideRead, columnCount, grouping, fontSize, fontWeight,
        setHideRead, setColumnCount, setGrouping, setFontSize, setFontWeight,
        resetToDefaults,
      }}
    >
      {children}
    </ViewSettingsContext.Provider>
  );
}

export function useViewSettings() {
  return useContext(ViewSettingsContext);
}
